// The imports we need from both aws-sdk and the cognito js library
import { Config, CognitoIdentityCredentials } from "aws-sdk";
import { CognitoUser, CognitoUserPool, AuthenticationDetails, CognitoUserAttribute } from "amazon-cognito-identity-js";

import config from "@/config";
import { resolve } from "@sentry/utils";

// We'll want to create a class for CognitoAuth which will
// contain all the methods we will need within our App
export default class CognitoAuth {
  // The constructor for this class will initialize our userSession
  // as null
  constructor() {
    this.userSession = null;
  }

  isAuthenticated(cb) {
    let cognitoUser = this.getCurrentUser();
    if (cognitoUser != null) {
      cognitoUser.getSession((err, session) => {
        if (err) {
          return cb(err, false);
        }
        return cb(session, true);
      });
    } else {
      cb(null, false);
    }
  }

  // this will set up our app to use cognito to use
  // the user pool that we'll be creating later on
  configure(config) {
    if (typeof config !== "object" || Array.isArray(config)) {
      throw new Error("[CognitoAuth error] valid option object required");
    }
    this.userPool = new CognitoUserPool({
      UserPoolId: config.IdentityPoolId,
      ClientId: config.ClientId,
    });
    Config.region = config.region;
    Config.credentials = new CognitoIdentityCredentials({
      IdentityPoolId: config.IdentityPoolId,
    });
    this.options = config;
  }

  // a signup function which will allow new people
  // to create an account in our app
  signup(username, email, pass, name = "", cb) {
    let attributeList = [
      new CognitoUserAttribute({
        Name: "email",
        Value: email,
      }),
      new CognitoUserAttribute({
        Name: "name",
        Value: name,
      }),
      new CognitoUserAttribute({
        Name: "nickname",
        Value: email,
      }),
      new CognitoUserAttribute({
        Name: "gender",
        Value: "thing",
      }),
      new CognitoUserAttribute({
        Name: "birthdate",
        Value: "01/01/1970",
      }),
    ];

    this.userPool.signUp(username, pass, attributeList, null, cb);
  }

  // a function that will allow existing users to
  // authenticate with our application
  authenticate(username, pass, cb) {
    let authenticationData = { Username: username, Password: pass };
    let authenticationDetails = new AuthenticationDetails(authenticationData);
    let userData = { Username: username, Pool: this.userPool };
    let cognitoUser = new CognitoUser(userData);

    cognitoUser.authenticateUser(authenticationDetails, {
      onSuccess: function (result) {
        var logins = {};
        logins["cognito-idp." + config.region + ".amazonaws.com/" + config.UserPoolId] = result.getIdToken().getJwtToken();

        Config.credentials = new CognitoIdentityCredentials({
          IdentityPoolId: config.UserPoolId,
          Logins: logins,
        });
        cb(null, result);
      },
      onFailure: function (err) {
        cb(err);
      },
      newPasswordRequired: function (userAttributes, requiredAttributes) {
        console.log("New Password Is Required");
      },
    });
  }

  // a helper function that allows us to
  // get the information for the current user
  getCurrentUser() {
    return this.userPool.getCurrentUser();
  }

  // a function that allows us to confirm newly
  // registered users of our app
  confirmRegistration(username, code, cb) {
    let cognitoUser = new CognitoUser({
      Username: username,
      Pool: this.userPool,
    });
    cognitoUser.confirmRegistration(code, true, cb);
  }

  resendConfirmationCode(username, cb) {
    let cognitoUser = new CognitoUser({
      Username: username,
      Pool: this.userPool,
    });
    cognitoUser.resendConfirmationCode(cb);
  }

  // does what it says on the tin, allows users
  // to logout if they are already logged in
  logout() {
    this.getCurrentUser().signOut();
  }

  async forgotPassword(username) {
    let user = new CognitoUser({
      Username: username,
      Pool: this.userPool,
    });
    return new Promise((resolve, reject) =>
      user.forgotPassword({
        onSuccess: (response) => {
          resolve(response);
        },
        onFailure: (err) => {
          reject(err);
        },
        inputVerificationCode: () => {
          resolve("verify");
        },
      })
    );
  }

  async updatePassword(username, verificationCode, newPassword) {
    let cognitoUser = new CognitoUser({
      Username: username,
      Pool: this.userPool,
    });

    return new Promise((resolve, reject) => {
      cognitoUser.confirmPassword(verificationCode, newPassword, {
        onFailure(err) {
          reject(err);
        },
        onSuccess() {
          resolve("true");
        },
      });
    });
  }

  // Retrieve the users current token if they have
  // a session, otherwise returns null
  async getIdToken() {
    return new Promise((resolve, reject) => {
      if (this.getCurrentUser() == null) {
        reject();
      }

      this.getCurrentUser().getSession((err, session) => {
        if (err) return reject(err);
        if (session.isValid()) {
          return resolve(session.getIdToken().getJwtToken());
        }
        reject("Session is invalid");
      });
    });
  }

  async getUserGroup(token) {
    var jwtDecode = require("jwt-decode");
    const tokenDecoded = jwtDecode(token);
    return tokenDecoded["cognito:groups"];
  }
}

// This installed CognitoAuth into our Vue instance
CognitoAuth.install = function (Vue, options) {
  Object.defineProperty(Vue.prototype, "$cognitoAuth", {
    get() {
      return this.$root._cognitoAuth;
    },
  });

  Vue.mixin({
    beforeCreate() {
      if (this.$options.cognitoAuth) {
        this._cognitoAuth = this.$options.cognitoAuth;
        this._cognitoAuth.configure(options);
      }
    },
  });
};
